import React, { useState } from 'react';
import { X, Calculator, TrendingUp } from 'lucide-react';

const CalculatorModal = ({ isOpen, onClose }) => {
  const [calculatorType, setCalculatorType] = useState('sip');
  const [formData, setFormData] = useState({
    monthlyInvestment: '',
    timePeriodYears: '',
    expectedAnnualReturn: '',
    presentValue: '',
    targetAmount: ''
  });
  
  const [results, setResults] = useState(null);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const calculateReturns = () => {
    let calculationResults = {};
    
    if (calculatorType === 'sip') {
      calculationResults = calculateSIP();
    } else if (calculatorType === 'future_value') {
      calculationResults = calculateFutureValue();
    } else if (calculatorType === 'wealth') {
      calculationResults = calculateWealthPlanning();
    }
    
    setResults(calculationResults);
  };

  const calculateSIP = () => {
    const monthlyAmount = parseFloat(formData.monthlyInvestment);
    const years = parseInt(formData.timePeriodYears);
    const annualRate = parseFloat(formData.expectedAnnualReturn) / 100;
    const monthlyRate = annualRate / 12;
    const totalMonths = years * 12;

    const futureValue = monthlyAmount * (((1 + monthlyRate) ** totalMonths - 1) / monthlyRate);
    const totalInvestment = monthlyAmount * totalMonths;
    const totalReturns = futureValue - totalInvestment;

    return {
      calculator_type: 'SIP Calculator',
      total_investment: totalInvestment,
      maturity_value: futureValue,
      total_returns: totalReturns,
      monthly_withdrawal_at_8_percent: (futureValue * 0.08) / 12,
      monthly_withdrawal_at_10_percent: (futureValue * 0.10) / 12
    };
  };

  const calculateFutureValue = () => {
    const presentValue = parseFloat(formData.presentValue);
    const years = parseInt(formData.timePeriodYears);
    const annualRate = parseFloat(formData.expectedAnnualReturn) / 100;

    const futureValue = presentValue * ((1 + annualRate) ** years);
    const totalReturns = futureValue - presentValue;

    return {
      calculator_type: 'Future Value Calculator',
      present_value: presentValue,
      future_value: futureValue,
      total_returns: totalReturns
    };
  };

  const calculateWealthPlanning = () => {
    const targetAmount = parseFloat(formData.targetAmount);
    const years = parseInt(formData.timePeriodYears);
    const annualRate = parseFloat(formData.expectedAnnualReturn) / 100;
    const monthlyRate = annualRate / 12;
    const totalMonths = years * 12;

    const requiredMonthlySIP = targetAmount * monthlyRate / (((1 + monthlyRate) ** totalMonths) - 1);
    const totalInvestment = requiredMonthlySIP * totalMonths;

    return {
      calculator_type: 'Wealth Planning Calculator',
      target_amount: targetAmount,
      required_monthly_sip: requiredMonthlySIP,
      total_investment: totalInvestment,
      time_period: years
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const closeModal = () => {
    setResults(null);
    setFormData({
      monthlyInvestment: '',
      timePeriodYears: '',
      expectedAnnualReturn: '',
      presentValue: '',
      targetAmount: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
              <Calculator className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Financial Calculator</h2>
          </div>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Calculator Type Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <button
              onClick={() => {
                setCalculatorType('sip');
                setResults(null);
              }}
              className={`p-4 rounded-lg border-2 transition-all ${
                calculatorType === 'sip'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <TrendingUp className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <h3 className="font-semibold">SIP Calculator</h3>
                <p className="text-sm text-gray-600">Calculate SIP returns</p>
              </div>
            </button>

            <button
              onClick={() => {
                setCalculatorType('future_value');
                setResults(null);
              }}
              className={`p-4 rounded-lg border-2 transition-all ${
                calculatorType === 'future_value'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <TrendingUp className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <h3 className="font-semibold">Future Value</h3>
                <p className="text-sm text-gray-600">Calculate future value</p>
              </div>
            </button>

            <button
              onClick={() => {
                setCalculatorType('wealth');
                setResults(null);
              }}
              className={`p-4 rounded-lg border-2 transition-all ${
                calculatorType === 'wealth'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <TrendingUp className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <h3 className="font-semibold">Wealth Planning</h3>
                <p className="text-sm text-gray-600">Plan target wealth</p>
              </div>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Form */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {calculatorType === 'sip' && 'SIP Calculator Parameters'}
                {calculatorType === 'future_value' && 'Future Value Parameters'}
                {calculatorType === 'wealth' && 'Wealth Planning Parameters'}
              </h3>

              <div className="space-y-4">
                {calculatorType === 'sip' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Investment (₹)
                      </label>
                      <input
                        type="number"
                        name="monthlyInvestment"
                        value={formData.monthlyInvestment}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="25,000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Years)</label>
                      <input
                        type="number"
                        name="timePeriodYears"
                        value={formData.timePeriodYears}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="10"
                        min="1"
                        max="50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expected Annual Returns (%)</label>
                      <input
                        type="number"
                        name="expectedAnnualReturn"
                        value={formData.expectedAnnualReturn}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="12"
                        min="1"
                        max="30"
                        step="0.1"
                      />
                    </div>
                  </>
                )}

                {calculatorType === 'future_value' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Present Value (₹)</label>
                      <input
                        type="number"
                        name="presentValue"
                        value={formData.presentValue}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="10,00,000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Years)</label>
                      <input
                        type="number"
                        name="timePeriodYears"
                        value={formData.timePeriodYears}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="10"
                        min="1"
                        max="50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expected Annual Returns (%)</label>
                      <input
                        type="number"
                        name="expectedAnnualReturn"
                        value={formData.expectedAnnualReturn}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="12"
                        min="1"
                        max="30"
                        step="0.1"
                      />
                    </div>
                  </>
                )}

                {calculatorType === 'wealth' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Target Amount (₹)</label>
                      <input
                        type="number"
                        name="targetAmount"
                        value={formData.targetAmount}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="1,00,00,000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Years)</label>
                      <input
                        type="number"
                        name="timePeriodYears"
                        value={formData.timePeriodYears}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="15"
                        min="1"
                        max="50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expected Annual Returns (%)</label>
                      <input
                        type="number"
                        name="expectedAnnualReturn"
                        value={formData.expectedAnnualReturn}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="12"
                        min="1"
                        max="30"
                        step="0.1"
                      />
                    </div>
                  </>
                )}

                <button
                  onClick={calculateReturns}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  <Calculator className="h-5 w-5 mr-2" />
                  Calculate Returns
                </button>
              </div>
            </div>

            {/* Results */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Results</h3>
              
              {!results ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    Enter your parameters and click calculate to see results.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 rounded-lg text-white">
                    <h4 className="font-bold mb-2">{results.calculator_type} Results</h4>
                  </div>

                  {calculatorType === 'sip' && (
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Total Investment</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(results.total_investment)}
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-sm text-green-700">Maturity Value</p>
                        <p className="text-xl font-bold text-green-800">
                          {formatCurrency(results.maturity_value)}
                        </p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-blue-700">Total Returns</p>
                        <p className="text-lg font-bold text-blue-800">
                          {formatCurrency(results.total_returns)}
                        </p>
                      </div>
                    </div>
                  )}

                  {calculatorType === 'future_value' && (
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Present Value</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(results.present_value)}
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-sm text-green-700">Future Value</p>
                        <p className="text-xl font-bold text-green-800">
                          {formatCurrency(results.future_value)}
                        </p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-blue-700">Total Returns</p>
                        <p className="text-lg font-bold text-blue-800">
                          {formatCurrency(results.total_returns)}
                        </p>
                      </div>
                    </div>
                  )}

                  {calculatorType === 'wealth' && (
                    <div className="space-y-3">
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-sm text-green-700">Target Amount</p>
                        <p className="text-xl font-bold text-green-800">
                          {formatCurrency(results.target_amount)}
                        </p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-blue-700">Required Monthly SIP</p>
                        <p className="text-lg font-bold text-blue-800">
                          {formatCurrency(results.required_monthly_sip)}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">Total Investment</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(results.total_investment)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-center pt-6 border-t border-gray-200 mt-6">
            <button
              onClick={closeModal}
              className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Close Calculator
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculatorModal;